import { SparklesIcon } from "lucide-react";
import { useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode, type Ref } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { useResaltadoDeSincronizaciones } from "@/hooks/useResaltadoDeSincronizaciones";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRellenarAguasConIa, useResolverElRelleno } from "@/hooks/useRellenoConIa";
import { getApiErrorMessage } from "@/lib/api";
import {
  ETIQUETA_FUENTE_DE_AGUA,
  ETIQUETA_INSCRIPCION_DEL_POZO,
  ETIQUETA_OBRA_DE_AGUA,
  type EstadoDeInscripcionDelPozo,
  type GuardarAguasInput,
  type Informe,
  type TipoDeFuenteDeAgua,
  type TipoDeObraDeAgua,
} from "@/lib/informes";
import type { FuenteDeLaPropuesta as Fuente } from "@/lib/rellenoConIa";
import { cn } from "@/lib/utils";

import { BarraDeGuardado } from "./BarraDeGuardado";
import { marcaDeFila } from "./MarcasDeSincronizacion";
import { DescripcionDeLasAguas } from "./DescripcionDeLasAguas";
import { modoDelTerreno } from "./cuadroDeConstrucciones";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { VisorDeFotos } from "./FotosDeTerreno";
import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";
import { LineaDeTerrenoDelAgua } from "./LineaDeTerrenoDelAgua";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import {
  AvisoDeLoRellenado,
  BotonDeRellenoConIa,
  type AvisoDelRelleno,
} from "./RellenoConIa";
import { resumenDeLoPropuesto, sumarLaPropuesta } from "./aguasConIa";
import { contarValidadas, textoDeLasValidadas } from "./cuadroDeSuelos";
import {
  esBorradorDeTerreno,
  filaDeFuenteDesdeElItem,
  filaDeObraDesdeElItem,
  itemDeLaFila,
  itemsPorEnlazarDeAguas,
  opcionesParaVincular,
  seccionDeAguas,
  tituloDeLaFila,
  tituloDelAviso,
  type ClaseDeAgua,
} from "./aguasTerreno";
import { fotosDelVisor } from "./registroDeTerreno";
import type { EstadoDelTerreno } from "./terrenoDeAntecedentes";
import {
  contarCambiosDeAguas,
  filasDesdeFuentes,
  filasDesdeObras,
  fuenteDeAguaVacia,
  inputDesdeElFormulario,
  obraDeAguaVacia,
  totalDeAguaDisponible,
  totalEscrito,
  type FilaDeFuenteDeAgua,
  type FilaDeObraDeAgua,
} from "./aguas";

export interface PestanaDeAguasHandle {
  guardar: () => void;
  /**
   * `guardado`: el informe tal como lo dejó el servidor. Hace falta porque las
   * filas nuevas recién tienen id ahí, y el servidor reconcilia por id: sin
   * releerlas, un segundo guardado las crearía otra vez.
   */
  marcarGuardado: (guardado?: Informe) => void;
}

interface BorradorDeAguas {
  noAplica: boolean;
  fuentes: FilaDeFuenteDeAgua[];
  obras: FilaDeObraDeAgua[];
  notas: string;
  /** La descripción general: vive en `informe_secciones`, como en las otras pestañas técnicas. */
  descripcion: string;
  descripcionValidada: boolean;
}

interface PestanaDeAguasProps {
  informe: Informe;
  guardando: boolean;
  ref?: Ref<PestanaDeAguasHandle>;
  onCambios?: (hayCambios: boolean) => void;
  onGuardar: (input: GuardarAguasInput) => void;
  /**
   * Lo que el encargado sincronizó desde la App de terreno. Solo se LEE: las
   * fuentes y obras que creó llegan como filas TERRENO y lo registrado en cada
   * fila se ve bajo ella. Sin él (o con el módulo apagado) la pestaña se
   * dibuja sin ninguna pieza de terreno.
   */
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita: da el nombre cuando todavía no sincroniza. */
  asignado?: string | null;
  /** Abre la pestaña "Registro de terreno". */
  onVerTerreno?: () => void;
}

/**
 * La pestaña "Descriptor de aguas": de dónde sale el agua del predio, cuánta
 * hay y con qué obras se aprovecha.
 *
 * NO ES FORMULARIO + TABLA como maquinaria, instalaciones, construcciones y
 * plantaciones, y la diferencia no es de gusto. Aquellas guardan fila por fila
 * porque de cada una cuelgan FOTOS en el bucket; de una fuente de agua no
 * cuelga nada, así que las dos listas se editan en línea y se guardan enteras
 * con el resto de la pestaña — el patrón del cuadro de clases de uso de suelo.
 *
 * LAS FUENTES VAN EN UNA GRILLA DE COLUMNAS FIJAS, como pide el diseño, aunque
 * sus filas no comparten todos los campos: una fuente superficial se describe
 * con el canal, sus acciones y la equivalencia; una subterránea, con el estado
 * de inscripción del pozo (que ocupa la celda del canal). Lo que no corresponde
 * al tipo se dibuja como "—" y no como un input vacío: ese vacío no significa
 * "sin medir" sino "no aplica". El caudal está en las dos clases porque es la
 * única columna que se suma.
 *
 * EL CAUDAL DEL CANAL SE ESCRIBE, NO SE CALCULA, apartándose del diseño (que lo
 * deriva como acciones × equivalencia): el modelo guarda los tres números
 * independientes y el que manda es el del certificado. Pendiente de confirmar
 * con el stakeholder.
 *
 * LA DESCRIPCIÓN GENERAL ES LA TARJETA 01 desde el 08-10-2026 (decisión de
 * Rodgers: el diseño la dibuja y la app de terreno ya la capturaba). Hasta el
 * 13-09-2026 esta era la única pestaña técnica sin ella. Se esconde con «sin
 * agua», igual que las dos listas, pero su texto sigue en el borrador. No
 * ofrece «Mejorar redacción»: ver {@see DescripcionDeLasAguas}.
 *
 * UN SOLO GUARDADO PARA TODO, al revés que las pestañas con fotos: acá no hay
 * nada que se vaya en el acto, los dos cuadros son texto y viajan con las notas
 * y la casilla en el mismo cuerpo. El botón vive en la barra fija del pie, con
 * el conteo de lo que está sin guardar.
 */
export function PestanaDeAguas({
  informe,
  guardando,
  ref,
  onCambios,
  onGuardar,
  terreno,
  asignado = null,
  onVerTerreno,
}: PestanaDeAguasProps) {
  const [borrador, setBorrador] = useState<BorradorDeAguas>(() => borradorDesde(informe));
  const [base, setBase] = useState<BorradorDeAguas>(() => borradorDesde(informe));

  const rellenar = useRellenarAguasConIa(informe.id);
  const resolverElRelleno = useResolverElRelleno();

  /* --- Lo de terreno --- */

  const registroDeTerreno = terreno?.estado === "ok" ? terreno.registro : undefined;
  const modoDeTerreno = modoDelTerreno(terreno);
  const seccionDeTerreno = seccionDeAguas(registroDeTerreno);
  const itemsDeTerreno = seccionDeTerreno?.items ?? [];
  const resaltado = useResaltadoDeSincronizaciones();
  const porEnlazar = useMemo(() => itemsPorEnlazarDeAguas(registroDeTerreno), [registroDeTerreno]);
  const fotosDelRegistro = useMemo(
    () => (seccionDeTerreno ? fotosDelVisor([seccionDeTerreno], "todo") : []),
    [seccionDeTerreno],
  );

  /**
   * Los uuid de las filas TERRENO que se quitaron del formulario.
   *
   * "Quitar" una fila que nació en la App solo la esconde hasta recargar: el
   * registro es del encargado y esta pantalla no lo borra. No cuenta como
   * cambio sin guardar porque no hay nada que guardar.
   */
  const [ocultas, setOcultas] = useState<string[]>([]);
  /** Las filas con su registro de terreno desplegado, por `clave`. */
  const [abiertas, setAbiertas] = useState<string[]>([]);
  const [fotoAbierta, setFotoAbierta] = useState<string | null>(null);

  /**
   * El borrador de AHORA, para el `onSuccess` del relleno.
   *
   * LEER `borrador` AHÍ SERÍA EL DE HACE VARIOS SEGUNDOS, y eso es pérdida de
   * trabajo silenciosa: leer el expediente tarda, y quien apretó el botón sigue
   * escribiendo mientras espera. Con el valor capturado en el clic, la mezcla
   * se calcularía sobre un borrador viejo y `escribir()` PISARÍA las filas que
   * la persona cargó entre medio — sin un solo error, y sobre una pantalla que
   * acaba de decir que agregó cosas.
   *
   * Se actualiza en CADA render, que es el mismo patrón con el que
   * `useAccionDemorada` evita que un temporizador viejo escriba sobre estado
   * nuevo.
   */
  const borradorAhora = useRef(borrador);
  borradorAhora.current = borrador;

  const [aviso, setAviso] = useState<AvisoDelRelleno | null>(null);
  const [errorDelRelleno, setErrorDelRelleno] = useState<string | null>(null);

  /**
   * La corrida en curso y qué filas trajo.
   *
   * LAS CLAVES SON LO QUE HACE POSIBLE DESHACER. Sin ellas, "descartar lo
   * propuesto" tendría que adivinar cuáles filas puso la IA, y la única forma
   * de adivinar sería mirar el contenido — que la persona ya pudo haber
   * corregido, que es justo lo que se quiere conservar.
   */
  const [ultimoRelleno, setUltimoRelleno] = useState<{
    ejecucionId: number;
    claves: string[];
    /**
     * De qué documento dice la IA que salió cada fila, por clave. Solo se
     * dibuja mientras esta corrida siga pendiente: no se guarda con los datos.
     */
    fuentes: Record<string, Fuente | null>;
  } | null>(null);

  const cambios = contarCambiosDeAguas(borrador, base);
  const hayCambios = cambios > 0;

  useEffect(() => {
    onCambios?.(hayCambios);
  }, [hayCambios, onCambios]);

  useEffect(() => () => onCambios?.(false), [onCambios]);

  useImperativeHandle(ref, () => ({
    guardar,
    marcarGuardado: (guardado) => {
      // Con el informe que devolvió el servidor las filas nuevas ganan su id.
      const guardadoComoBorrador: BorradorDeAguas =
        guardado?.agua_fuentes && guardado.agua_obras
          ? {
              ...borrador,
              fuentes: filasDesdeFuentes(guardado.agua_fuentes),
              obras: filasDesdeObras(guardado.agua_obras),
            }
          : borrador;

      setBorrador(guardadoComoBorrador);
      setBase(guardadoComoBorrador);

      // EL GUARDADO ES LO QUE ACEPTA LA PROPUESTA, y no el clic que la trajo:
      // hasta acá era un borrador que la persona podía descartar. Es el mismo
      // criterio con el que el importador de tasaciones marca `aceptado` al
      // crear el informe y no al leer el PDF.
      //
      // Se avisa y se OLVIDA la corrida: lo que quedó guardado ya no es una
      // propuesta pendiente, y un segundo guardado no puede volver a
      // aceptarla.
      if (ultimoRelleno) {
        resolverElRelleno.mutate({
          ejecucionId: ultimoRelleno.ejecucionId,
          resultado: "aceptado",
        });
        setUltimoRelleno(null);
        setAviso(null);
      }
    },
  }));

  function pedirElRelleno() {
    setErrorDelRelleno(null);
    setAviso(null);

    rellenar.mutate(undefined, {
      onSuccess: ({ ejecucion_id, propuesta, documentos, pendientes, sin_usar }) => {
        const actual = borradorAhora.current;
        const mezcla = sumarLaPropuesta(actual, propuesta);

        escribir({ fuentes: mezcla.fuentes, obras: mezcla.obras });

        setUltimoRelleno({
          ejecucionId: ejecucion_id,
          // Solo las filas NUEVAS: las que ya estaban son de la persona y
          // "descartar" no puede llevárselas.
          claves: [
            ...mezcla.fuentes.slice(actual.fuentes.length).map((fila) => fila.clave),
            ...mezcla.obras.slice(actual.obras.length).map((fila) => fila.clave),
          ],
          fuentes: mezcla.fuentesPorClave,
        });

        setAviso({
          resumen: resumenDeLoPropuesto(mezcla.fuentesAgregadas, mezcla.obrasAgregadas),
          documentos,
          pendientes,
          sinUsar: sin_usar,
        });
      },

      // El error se muestra ACÁ y no en un toast: habla de esta pestaña y de lo
      // que hay que hacer para resolverlo —adjuntar un certificado, esperar a
      // que se transcriba un escaneo—, y un toast se va antes de que la persona
      // termine de leerlo.
      onError: (e) =>
        setErrorDelRelleno(
          getApiErrorMessage(
            e,
            "No pudimos leer el expediente. Inténtalo de nuevo o completa la pestaña a mano.",
          ),
        ),
    });
  }

  /**
   * Quita las filas que trajo el último relleno y lo anota como descartado.
   *
   * UN DESCARTE INFORMA TANTO COMO UNA ACEPTACIÓN: sin él, `ia_ejecuciones`
   * solo tendría los casos en que la IA acertó, y esa proporción es lo único
   * que permite decidir si la función vale lo que cuesta.
   */
  function descartarElRelleno() {
    if (!ultimoRelleno) return;

    const propuestas = new Set(ultimoRelleno.claves);

    escribir({
      fuentes: borrador.fuentes.filter((fila) => !propuestas.has(fila.clave)),
      obras: borrador.obras.filter((fila) => !propuestas.has(fila.clave)),
    });

    resolverElRelleno.mutate({
      ejecucionId: ultimoRelleno.ejecucionId,
      resultado: "descartado",
    });

    setUltimoRelleno(null);
    setAviso(null);
  }

  function escribir(cambio: Partial<BorradorDeAguas>) {
    setBorrador((actual) => ({ ...actual, ...cambio }));
  }

  function guardar() {
    onGuardar(
      inputDesdeElFormulario(
        borrador.noAplica,
        borrador.fuentes,
        borrador.obras,
        borrador.notas,
        borrador.descripcion,
        borrador.descripcionValidada,
      ),
    );
  }

  /** Vuelve la pestaña a lo último guardado y suelta lo que la IA propuso. */
  function descartarLosCambios() {
    setBorrador(base);
    setOcultas([]);

    if (ultimoRelleno) {
      resolverElRelleno.mutate({ ejecucionId: ultimoRelleno.ejecucionId, resultado: "descartado" });
      setUltimoRelleno(null);
    }

    setAviso(null);
  }

  const hayFilas = borrador.fuentes.length > 0 || borrador.obras.length > 0;
  const total = totalDeAguaDisponible(borrador.fuentes);

  /* Las filas de terreno que todavía son BORRADORES: el ítem existe en la App y
     la pestaña no lo ha adoptado. Una fila que ya está en el formulario (o a la
     que se le dijo "es la misma que") no se ofrece dos veces. */
  const enUso = new Set(
    [...borrador.fuentes, ...borrador.obras].flatMap((fila) => (fila.terreno_ref ? [fila.terreno_ref] : [])),
  );
  const sueltas = (uuid: string) => !enUso.has(uuid) && !ocultas.includes(uuid);
  const borradoresDeFuentes = porEnlazar.fuentes
    .filter((item) => sueltas(item.uuid))
    .map(filaDeFuenteDesdeElItem);
  const borradoresDeObras = porEnlazar.obras
    .filter((item) => sueltas(item.uuid))
    .map(filaDeObraDesdeElItem);
  const filasDeFuentes = [...borrador.fuentes, ...borradoresDeFuentes];
  const filasDeObras = [...borrador.obras, ...borradoresDeObras];

  /* El aviso cuenta lo que sigue sin resolver: ni escondido ni asignado a una
     fila que ya existe. */
  const asignadasAFilasGuardadas = new Set(
    [...borrador.fuentes, ...borrador.obras].flatMap((fila) =>
      fila.id !== null && fila.terreno_ref ? [fila.terreno_ref] : [],
    ),
  );
  const pendientesDeTerreno = [...porEnlazar.fuentes, ...porEnlazar.obras].filter(
    (item) => !ocultas.includes(item.uuid) && !asignadasAFilasGuardadas.has(item.uuid),
  ).length;

  function ocultar(uuid: string | null) {
    if (uuid) setOcultas((actuales) => [...actuales, uuid]);
  }

  function alternarRegistro(clave: string) {
    setAbiertas((actuales) =>
      actuales.includes(clave) ? actuales.filter((otra) => otra !== clave) : [...actuales, clave],
    );
  }

  /** Editar un borrador de terreno lo ADOPTA: desde ahí viaja con la pestaña. */
  function cambiarFuente(clave: string, cambio: Partial<FilaDeFuenteDeAgua>) {
    if (borrador.fuentes.some((otra) => otra.clave === clave)) {
      escribir({
        fuentes: borrador.fuentes.map((otra) => (otra.clave === clave ? { ...otra, ...cambio } : otra)),
      });

      return;
    }

    const borradorDeTerreno = borradoresDeFuentes.find((otra) => otra.clave === clave);
    if (borradorDeTerreno) escribir({ fuentes: [...borrador.fuentes, { ...borradorDeTerreno, ...cambio }] });
  }

  function cambiarObra(clave: string, cambio: Partial<FilaDeObraDeAgua>) {
    if (borrador.obras.some((otra) => otra.clave === clave)) {
      escribir({
        obras: borrador.obras.map((otra) => (otra.clave === clave ? { ...otra, ...cambio } : otra)),
      });

      return;
    }

    const borradorDeTerreno = borradoresDeObras.find((otra) => otra.clave === clave);
    if (borradorDeTerreno) escribir({ obras: [...borrador.obras, { ...borradorDeTerreno, ...cambio }] });
  }

  function quitarFuente(fila: FilaDeFuenteDeAgua) {
    escribir({ fuentes: borrador.fuentes.filter((otra) => otra.clave !== fila.clave) });
    if (esBorradorDeTerreno(fila)) ocultar(fila.terreno_ref);
  }

  function quitarObra(fila: FilaDeObraDeAgua) {
    escribir({ obras: borrador.obras.filter((otra) => otra.clave !== fila.clave) });
    if (esBorradorDeTerreno(fila)) ocultar(fila.terreno_ref);
  }

  /**
   * "Es la misma que X": el registro de terreno pasa a colgar de la fila X y el
   * borrador desaparece. Se guarda con la pestaña, como todo lo demás.
   */
  function vincularFuente(fila: FilaDeFuenteDeAgua, objetivo: string) {
    escribir({
      fuentes: borrador.fuentes
        .filter((otra) => otra.clave !== fila.clave)
        .map((otra) => (otra.clave === objetivo ? { ...otra, terreno_ref: fila.terreno_ref } : otra)),
    });
  }

  function vincularObra(fila: FilaDeObraDeAgua, objetivo: string) {
    escribir({
      obras: borrador.obras
        .filter((otra) => otra.clave !== fila.clave)
        .map((otra) => (otra.clave === objetivo ? { ...otra, terreno_ref: fila.terreno_ref } : otra)),
    });
  }

  /** La línea de terreno de una fila, o `null` si no tiene nada que decir. */
  function marcaDeLaFila(clase: ClaseDeAgua, fila: FilaDeFuenteDeAgua | FilaDeObraDeAgua) {
    return marcaDeFila(resaltado.deItem(itemDeLaFila(itemsDeTerreno, clase, fila)));
  }

  function lineaDeTerreno(clase: ClaseDeAgua, fila: FilaDeFuenteDeAgua | FilaDeObraDeAgua, posicion: number) {
    const que = `${clase === "fuente" ? "la fuente" : "la obra"} ${posicion}`;
    const item = itemDeLaFila(itemsDeTerreno, clase, fila);
    const opciones = opcionesParaVincular(clase === "fuente" ? borrador.fuentes : borrador.obras);

    return (
      <LineaDeTerrenoDelAgua
        informeId={informe.id}
        clase={clase}
        que={que}
        fila={fila}
        titulo={tituloDeLaFila(clase, fila as FilaDeFuenteDeAgua)}
        item={item}
        modo={modoDeTerreno}
        abierto={abiertas.includes(fila.clave)}
        onAlternar={() => alternarRegistro(fila.clave)}
        opciones={opciones}
        onVincular={(objetivo) =>
          clase === "fuente"
            ? vincularFuente(fila as FilaDeFuenteDeAgua, objetivo)
            : vincularObra(fila as FilaDeObraDeAgua, objetivo)
        }
        onAbrirFoto={setFotoAbierta}
        encargado={registroDeTerreno?.encargado?.nombre ?? null}
      />
    );
  }

  const validadasDeFuentes = contarValidadas(borrador.fuentes);
  const validadasDeObras = contarValidadas(borrador.obras);

  return (
    <div className="flex flex-col gap-3.5">
      {/* La banda se esconde con "sin agua": proponerle filas a quien acaba de
          declarar que no hay ninguna sería contradecirlo. */}
      {borrador.noAplica ? null : (
        <section className="border-success-dot/25 bg-success-soft/50 flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-4 py-3">
          <span className="bg-success-soft text-success-soft-foreground flex size-8.5 flex-none items-center justify-center rounded-lg">
            <SparklesIcon className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-ink text-[0.8125rem] font-bold">
              Cargar aguas desde los documentos
            </h3>
            <p className="text-ink-muted mt-0.5 text-[0.71875rem] font-semibold text-pretty">
              Lee los certificados de derechos de agua de Antecedentes legales y agrega las fuentes
              que encuentre, con acciones y caudal. No valida nada.
            </p>
          </div>
          <div className="flex-none">
            <BotonDeRellenoConIa
              rellenando={rellenar.isPending}
              // Mientras la pestaña viaja al servidor, mezclar una propuesta
              // dejaría filas nuevas fuera del cuerpo que ya salió, y la pantalla
              // las mostraría como si estuvieran guardadas.
              disabled={guardando}
              variante="oscura"
              onRellenar={pedirElRelleno}
            />
          </div>
        </section>
      )}

      <AvisoDeLoRellenado
        rellenando={rellenar.isPending}
        error={errorDelRelleno}
        aviso={aviso}
        onDescartar={ultimoRelleno ? descartarElRelleno : null}
      />

      <section className="bg-card flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3">
        <label
          className={cn(
            "flex items-center gap-2.5",
            guardando || hayFilas ? "cursor-not-allowed opacity-55" : "cursor-pointer",
          )}
          title={hayFilas ? "Quita primero las fuentes y obras cargadas" : undefined}
        >
          <Checkbox
            checked={borrador.noAplica}
            // Con filas cargadas la casilla se APAGA en vez de borrarlas: el
            // servidor rechaza la contradicción, y resolverla borrando sería
            // perder trabajo por marcar una casilla.
            disabled={guardando || hayFilas}
            onCheckedChange={(valor) => escribir({ noAplica: valor === true })}
          />
          <span className="text-ink text-[0.8125rem] font-bold">
            El predio no cuenta con derechos de agua ni pozo
          </span>
        </label>

        {borrador.noAplica ? (
          <span className="text-ink-muted text-[0.71875rem] font-semibold">
            La sección se imprimirá indicando que no hay agua disponible.
          </span>
        ) : hayFilas ? (
          <span className="text-ink-muted text-[0.71875rem] font-semibold">
            Para marcarlo, quita antes las fuentes y obras cargadas.
          </span>
        ) : null}
      </section>

      {pendientesDeTerreno > 0 && !borrador.noAplica ? (
        <section
          aria-label="Fuentes y obras creadas en terreno"
          className="bg-info-soft/50 border-info-dot/30 flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3"
        >
          <span aria-hidden="true" className="bg-info-dot size-2 flex-none rounded-full" />
          <div className="min-w-0 flex-1">
            <h3 className="text-ink text-[0.8125rem] font-bold">
              {tituloDelAviso(pendientesDeTerreno)}
            </h3>
            <p className="text-ink-body mt-0.5 text-[0.71875rem] font-semibold text-pretty">
              Llegan en blanco desde la App, solo con su tipo. Completa los datos a partir de sus
              fotos y audios, o indica si es la misma que una ya cargada desde documentos.
            </p>
          </div>
        </section>
      ) : null}

      {borrador.noAplica ? null : (
        <>
          <DescripcionDeLasAguas
            informeId={informe.id}
            value={borrador.descripcion}
            onChange={(descripcion) => escribir({ descripcion })}
            validada={borrador.descripcionValidada}
            onValidada={(descripcionValidada) => escribir({ descripcionValidada })}
            disabled={guardando}
            terreno={terreno}
            asignado={asignado}
            onVerTerreno={onVerTerreno}
            onAbrirFoto={setFotoAbierta}
            // Lo escribió un modelo: nadie lo revisó, y la casilla anterior ya
            // no habla de este texto.
            onRedactado={(texto) => escribir({ descripcion: texto, descripcionValidada: false })}
          />

          <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
            <EncabezadoNumerado
              numero="02"
              idDelTitulo="aguas-fuentes-titulo"
              titulo="Fuentes de agua"
              bajada="El caudal de cada fuente se ingresa directo, según el certificado. El total suma todas."
              acciones={<ChipDeValidadas conteo={validadasDeFuentes} />}
            />

            <div className="border-border mt-3.5 overflow-x-auto rounded-lg border-[1.5px]">
              <div className="min-w-[52rem]">
                <div
                  className={cn(
                    COLUMNAS_DE_FUENTES,
                    "bg-superficie-sutil border-divisor border-b px-3 py-2",
                  )}
                >
                  <Encabezado>Tipo</Encabezado>
                  <Encabezado>Río, canal o pozo</Encabezado>
                  <Encabezado alineado="derecha">Acciones</Encabezado>
                  <Encabezado alineado="derecha">l/s por acción</Encabezado>
                  <Encabezado alineado="derecha">Caudal l/s</Encabezado>
                  <Encabezado alineado="centro">Valid.</Encabezado>
                  <span />
                </div>

                {filasDeFuentes.length === 0 ? (
                  <p className="text-ink-muted border-divisor border-b px-3 py-4 text-sm">
                    Todavía no hay fuentes cargadas. Agrega la primera con el botón de abajo.
                  </p>
                ) : (
                  filasDeFuentes.map((fila, indice) => (
                    <FilaDeFuente
                      key={fila.clave}
                      fila={fila}
                      posicion={indice + 1}
                      disabled={guardando}
                      fuenteDeLaPropuesta={ultimoRelleno?.fuentes[fila.clave]}
                      onCambiar={(cambio) => cambiarFuente(fila.clave, cambio)}
                      onQuitar={() => quitarFuente(fila)}
                      terreno={lineaDeTerreno("fuente", fila, indice + 1)}
                      marca={marcaDeLaFila("fuente", fila)}
                    />
                  ))
                )}

                {/* El total suma las DOS clases de fuente: `litros_seg` es la
                    única columna que comparten, justamente porque es la que se
                    suma. Las obras no entran — un tranque guarda agua, no la
                    aporta. Sin ningún caudal cargado imprime "—" y nunca 0. */}
                <div className="bg-superficie-sutil flex flex-wrap items-center gap-x-3.5 gap-y-2 px-3 py-2.5">
                  <BotonDeAgregar
                    disabled={guardando}
                    onClick={() => escribir({ fuentes: [...borrador.fuentes, fuenteDeAguaVacia()] })}
                  >
                    Agregar fuente de agua
                  </BotonDeAgregar>

                  <span className="text-ink-body ml-auto text-[0.78125rem] font-bold">
                    Total de agua disponible
                    <span className="text-ink ml-1.5 font-mono text-sm tabular-nums">
                      {totalEscrito(total)}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
            <EncabezadoNumerado
              numero="03"
              idDelTitulo="aguas-obras-titulo"
              titulo="Otras obras de aprovechamiento"
              bajada="Tranques, embalses u obras de impulsión. Describe la capacidad de cada una."
              acciones={<ChipDeValidadas conteo={validadasDeObras} />}
            />

            <div className="border-border mt-3.5 overflow-x-auto rounded-lg border-[1.5px]">
              <div className="min-w-[44rem]">
                <div
                  className={cn(
                    COLUMNAS_DE_OBRAS,
                    "bg-superficie-sutil border-divisor border-b px-3 py-2",
                  )}
                >
                  <Encabezado>Tipo de obra</Encabezado>
                  <Encabezado>Descripción de la capacidad</Encabezado>
                  <Encabezado alineado="centro">Valid.</Encabezado>
                  <span />
                </div>

                {filasDeObras.length === 0 ? (
                  <p className="text-ink-muted border-divisor border-b px-3 py-4 text-sm">
                    Todavía no hay obras cargadas. Agrega la primera con el botón de abajo.
                  </p>
                ) : (
                  filasDeObras.map((fila, indice) => (
                    <FilaDeObra
                      key={fila.clave}
                      fila={fila}
                      posicion={indice + 1}
                      disabled={guardando}
                      fuenteDeLaPropuesta={ultimoRelleno?.fuentes[fila.clave]}
                      onCambiar={(cambio) => cambiarObra(fila.clave, cambio)}
                      onQuitar={() => quitarObra(fila)}
                      terreno={lineaDeTerreno("obra", fila, indice + 1)}
                      marca={marcaDeLaFila("obra", fila)}
                    />
                  ))
                )}

                <div className="bg-superficie-sutil px-3 py-2.5">
                  <BotonDeAgregar
                    disabled={guardando}
                    onClick={() => escribir({ obras: [...borrador.obras, obraDeAguaVacia()] })}
                  >
                    Agregar obra de aprovechamiento
                  </BotonDeAgregar>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Con "sin agua" las dos listas desaparecen y las notas pasan a ser la
          primera tarjeta: el número sigue a lo que se ve. */}
      <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
        <EncabezadoNumerado
          numero={borrador.noAplica ? "01" : "04"}
          idDelTitulo="aguas-notas-titulo"
          titulo="Notas u observaciones"
          bajada="Opcional."
        />
        <div className="mt-3.5">
          <NotasDeLaPestana
            id="notas-agua"
            value={borrador.notas}
            disabled={guardando}
            onChange={(valor) => escribir({ notas: valor })}
            plana
          />
        </div>
      </section>

      <BarraDeGuardado
        cambios={cambios}
        enviando={guardando}
        etiquetaEnvio="Guardar cambios"
        ayuda="Las fuentes y obras se guardan con la pestaña."
        onGuardar={guardar}
        onDescartar={descartarLosCambios}
      />

      {registroDeTerreno ? (
        <VisorDeFotos
          informeId={informe.id}
          fotos={fotosDelRegistro}
          abierta={fotoAbierta}
          onCambiar={setFotoAbierta}
        />
      ) : null}
    </div>
  );
}

/* --- Piezas del diseño ----------------------------------------------------- */

/** Las siete columnas del cuadro de fuentes, tal como las mide el diseño. */
const COLUMNAS_DE_FUENTES =
  "grid grid-cols-[13.75rem_minmax(0,1fr)_6rem_7.5rem_6.875rem_3.75rem_4rem] items-center gap-2.5";

/** Las cuatro columnas del cuadro de obras. */
const COLUMNAS_DE_OBRAS = "grid grid-cols-[13.75rem_minmax(0,1fr)_3.75rem_4rem] items-center gap-2.5";

/** Los campos de texto en línea, con la altura de las demás tablas del expediente. */
const CAMPO = "h-9 min-w-0 rounded-[0.625rem] px-[0.6875rem] text-[0.8125rem]";
const CAMPO_NUMERICO = `${CAMPO} text-right font-mono font-bold tabular-nums`;
const SELECTOR = "w-full rounded-[0.625rem] px-2 text-xs font-bold";

function Encabezado({
  children,
  alineado = "izquierda",
}: {
  children: string;
  alineado?: "izquierda" | "centro" | "derecha";
}) {
  return (
    <span
      className={cn(
        "text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.04em] uppercase",
        alineado === "derecha" && "text-right",
        alineado === "centro" && "text-center",
      )}
    >
      {children}
    </span>
  );
}

/** "2 de 3 validadas", verde cuando están todas. */
function ChipDeValidadas({ conteo }: { conteo: { validadas: number; total: number } }) {
  const todas = conteo.total > 0 && conteo.validadas === conteo.total;

  return (
    <span
      className={cn(
        "rounded-[0.4375rem] px-2.5 py-[0.3125rem] font-mono text-[0.6875rem] font-bold",
        todas ? "bg-success-soft text-success-soft-foreground" : "bg-muted text-ink-body",
      )}
    >
      {textoDeLasValidadas(conteo)}
    </span>
  );
}

function BotonDeAgregar({
  children,
  disabled,
  onClick,
}: {
  children: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="text-success-soft-foreground hover:text-ink inline-flex items-center gap-2 text-[0.78125rem] font-bold disabled:opacity-50"
    >
      <span
        aria-hidden="true"
        className="border-success-dot/60 flex size-5.5 items-center justify-center rounded-[0.4375rem] border-[1.5px] border-dashed text-sm leading-none"
      >
        +
      </span>
      {children}
    </button>
  );
}

interface QuitarEnLineaProps {
  /** "la fuente 2": completa los nombres accesibles de los tres botones. */
  que: string;
  confirmando: boolean;
  disabled: boolean;
  onPedir: () => void;
  onCancelar: () => void;
  onConfirmar: () => void;
}

/**
 * "Quitar" con confirmación en la MISMA fila ("¿Quitar? Sí / No"), igual que el
 * cuadro de clases de suelo y los archivos de antecedentes: una fila cargada se
 * pierde sin deshacer hasta que se guarde la pestaña.
 */
function QuitarEnLinea({
  que,
  confirmando,
  disabled,
  onPedir,
  onCancelar,
  onConfirmar,
}: QuitarEnLineaProps) {
  return (
    <span className="flex justify-end">
      {confirmando ? (
        <span className="inline-flex items-center gap-1">
          <span className="sr-only">¿Quitar?</span>
          <button
            type="button"
            aria-label={`Sí, quitar ${que}`}
            onClick={onConfirmar}
            className="bg-destructive rounded-[0.4375rem] px-2 py-1 text-[0.71875rem] font-bold text-white"
          >
            Sí
          </button>
          <button
            type="button"
            aria-label={`No quitar ${que}`}
            onClick={onCancelar}
            className="text-ink-body border-input rounded-[0.4375rem] border-[1.5px] px-[0.4375rem] py-[0.1875rem] text-[0.71875rem] font-bold"
          >
            No
          </button>
        </span>
      ) : (
        <button
          type="button"
          disabled={disabled}
          aria-label={`Quitar ${que}`}
          onClick={onPedir}
          className="text-danger-soft-foreground hover:bg-danger-soft rounded-sm px-2 py-[0.3125rem] text-xs font-bold disabled:opacity-50"
        >
          Quitar
        </button>
      )}
    </span>
  );
}

interface CasillaDeValidacionProps {
  validada: boolean;
  nombre: string;
  disabled: boolean;
  onCambiar: (validada: boolean) => void;
}

/**
 * La casilla "Valid." de una fila.
 *
 * VA POR FILA Y NO UNA AL PIE, misma regla que el cuadro de clases de suelo:
 * con seis fuentes, una sola casilla no diría cuál de las seis se revisó, que
 * es justo lo que existe para garantizar. Y EDITAR una fila la desmarca: lo
 * validado era lo que decía antes.
 *
 * EXISTE ANTES QUE EL IMPORTADOR, a pedido del stakeholder (13-09-2026): el
 * dato lo va a proponer una carga automática, y alguien tiene que poder
 * hacerse cargo de cada fila cuando eso ocurra.
 */
function CasillaDeValidacion({ validada, nombre, disabled, onCambiar }: CasillaDeValidacionProps) {
  return (
    <span className="flex justify-center">
      <Checkbox
        checked={validada}
        disabled={disabled}
        title={validada ? "Quitar validación" : "Marcar como validada"}
        aria-label={nombre}
        onCheckedChange={(valor) => onCambiar(valor === true)}
      />
    </span>
  );
}

/* --- Una fuente ------------------------------------------------------------ */

interface FilaDeFuenteProps {
  fila: FilaDeFuenteDeAgua;
  posicion: number;
  disabled: boolean;
  /** `undefined`: la fila no es de la IA. `null`: la IA no indicó su fuente. */
  fuenteDeLaPropuesta?: Fuente | null;
  onCambiar: (cambio: Partial<FilaDeFuenteDeAgua>) => void;
  onQuitar: () => void;
  /** La línea de terreno de la fila (origen, contador, "es la misma que"). */
  terreno?: ReactNode;
  /** Si la fila recibió capturas de una sincronización resaltada. */
  marca?: ReturnType<typeof marcaDeFila>;
}

/**
 * Una fuente de agua, en línea.
 *
 * LAS CELDAS DEPENDEN DEL TIPO. Con "Agua superficial" se piden el canal, las
 * acciones y la equivalencia; con "Agua subterránea", el estado de inscripción
 * del pozo en la celda del canal y un guion en las dos numéricas. El caudal
 * está en los dos porque es el que se suma.
 *
 * CAMBIAR EL TIPO NO BORRA LO ESCRITO: lo que deja de verse sigue en el
 * borrador y vuelve si se elige el tipo de antes. Al guardar, el SERVIDOR lo
 * descarta —ver `InformeAguaFuente::limpiarSegunElTipo()`—, así que nada queda
 * persistido donde no corresponde y la persona no pierde lo que tecleó por
 * probar un selector.
 */
function FilaDeFuente({
  fila,
  posicion,
  disabled,
  fuenteDeLaPropuesta,
  onCambiar,
  onQuitar,
  terreno,
  marca,
}: FilaDeFuenteProps) {
  const [quitando, setQuitando] = useState(false);
  const esCanal = fila.tipo === "superficial_canal";

  /** Editar un dato desmarca la validación de ESTA fila: lo revisado era otro valor. */
  function editar(cambio: Partial<Omit<FilaDeFuenteDeAgua, "clave" | "validada">>) {
    onCambiar({ ...cambio, validada: false });
  }

  return (
    <div
      {...marca?.atributo}
      className={cn(
        "border-divisor border-b",
        quitando && "bg-danger-soft/50",
        marca?.claseFila,
        marca?.claseCelda,
      )}
    >
      {marca?.on ? <div className="px-3 pt-2">{marca.tag}</div> : null}
    <div className={cn(COLUMNAS_DE_FUENTES, "px-3 py-2")}>
      <Select
        value={fila.tipo}
        disabled={disabled}
        onValueChange={(valor) => editar({ tipo: valor as TipoDeFuenteDeAgua })}
      >
        <SelectTrigger size="sm" className={SELECTOR} aria-label={`Tipo de la fuente ${posicion}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(ETIQUETA_FUENTE_DE_AGUA) as TipoDeFuenteDeAgua[]).map((tipo) => (
            <SelectItem key={tipo} value={tipo}>
              {ETIQUETA_FUENTE_DE_AGUA[tipo]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {esCanal ? (
        <Input
          className={CAMPO}
          aria-label={`Río o canal de la fuente ${posicion}`}
          placeholder="Ej.: Canal Wodehouse"
          value={fila.rio_canal}
          disabled={disabled}
          onChange={(evento) => editar({ rio_canal: evento.target.value })}
        />
      ) : (
        <Select
          value={fila.estado_inscripcion}
          disabled={disabled}
          onValueChange={(valor) =>
            editar({ estado_inscripcion: valor as EstadoDeInscripcionDelPozo })
          }
        >
          <SelectTrigger
            size="sm"
            className={cn(SELECTOR, "font-semibold")}
            aria-label={`Estado de inscripción de la fuente ${posicion}`}
          >
            <SelectValue placeholder="Inscripción DGA · sin informar" />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(ETIQUETA_INSCRIPCION_DEL_POZO) as EstadoDeInscripcionDelPozo[]).map(
              (estado) => (
                <SelectItem key={estado} value={estado}>
                  {ETIQUETA_INSCRIPCION_DEL_POZO[estado]}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
      )}

      {/* `inputMode="decimal"` y no `type="number"`: en Chile se escribe con
          COMA y un input numérico la rechaza según el idioma del navegador. */}
      {esCanal ? (
        <Input
          className={CAMPO_NUMERICO}
          inputMode="decimal"
          aria-label={`N° de acciones de la fuente ${posicion}`}
          placeholder="0"
          value={fila.acciones}
          disabled={disabled}
          onChange={(evento) => editar({ acciones: evento.target.value })}
        />
      ) : (
        <SinDato />
      )}

      {esCanal ? (
        <Input
          className={CAMPO_NUMERICO}
          inputMode="decimal"
          aria-label={`Equivalencia de la fuente ${posicion}`}
          placeholder="0,00"
          value={fila.equivalencia_ls_accion}
          disabled={disabled}
          onChange={(evento) => editar({ equivalencia_ls_accion: evento.target.value })}
        />
      ) : (
        <SinDato />
      )}

      <Input
        className={CAMPO_NUMERICO}
        inputMode="decimal"
        aria-label={`Litros por segundo de la fuente ${posicion}`}
        placeholder="0,00"
        value={fila.litros_seg}
        disabled={disabled}
        onChange={(evento) => editar({ litros_seg: evento.target.value })}
      />

      <CasillaDeValidacion
        validada={fila.validada}
        nombre={`Validar la fuente ${posicion}`}
        disabled={disabled}
        onCambiar={(validada) => onCambiar({ validada })}
      />

      <QuitarEnLinea
        que={`la fuente ${posicion}`}
        confirmando={quitando}
        disabled={disabled}
        onPedir={() => setQuitando(true)}
        onCancelar={() => setQuitando(false)}
        onConfirmar={onQuitar}
      />

      {fuenteDeLaPropuesta === undefined ? null : (
        <div className="col-span-full">
          <FuenteDeLaPropuesta fuente={fuenteDeLaPropuesta} />
        </div>
      )}
    </div>
    {terreno}
    </div>
  );
}

/** La celda de un dato que NO corresponde al tipo de fuente: no es "sin medir". */
function SinDato() {
  return <span className="text-ink-ghost text-right font-mono text-xs">—</span>;
}

/* --- Una obra -------------------------------------------------------------- */

interface FilaDeObraProps {
  fila: FilaDeObraDeAgua;
  posicion: number;
  disabled: boolean;
  /** `undefined`: la fila no es de la IA. `null`: la IA no indicó su fuente. */
  fuenteDeLaPropuesta?: Fuente | null;
  onCambiar: (cambio: Partial<FilaDeObraDeAgua>) => void;
  onQuitar: () => void;
  terreno?: ReactNode;
  marca?: ReturnType<typeof marcaDeFila>;
}

/**
 * La capacidad de una obra es PROSA en una sola línea, como la dibuja el diseño:
 * se describe el uso y el estado además del volumen, y una obra de impulsión ni
 * siquiera se mide en metros cúbicos.
 */
function FilaDeObra({
  fila,
  posicion,
  disabled,
  fuenteDeLaPropuesta,
  onCambiar,
  onQuitar,
  terreno,
  marca,
}: FilaDeObraProps) {
  const [quitando, setQuitando] = useState(false);

  return (
    <div
      {...marca?.atributo}
      className={cn(
        "border-divisor border-b",
        quitando && "bg-danger-soft/50",
        marca?.claseFila,
        marca?.claseCelda,
      )}
    >
      {marca?.on ? <div className="px-3 pt-2">{marca.tag}</div> : null}
    <div className={cn(COLUMNAS_DE_OBRAS, "px-3 py-2")}>
      <Select
        value={fila.tipo}
        disabled={disabled}
        onValueChange={(valor) => onCambiar({ tipo: valor as TipoDeObraDeAgua, validada: false })}
      >
        <SelectTrigger size="sm" className={SELECTOR} aria-label={`Tipo de la obra ${posicion}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(ETIQUETA_OBRA_DE_AGUA) as TipoDeObraDeAgua[]).map((tipo) => (
            <SelectItem key={tipo} value={tipo}>
              {ETIQUETA_OBRA_DE_AGUA[tipo]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        className={CAMPO}
        aria-label={`Descripción de la capacidad de la obra ${posicion}`}
        placeholder="Ej.: tranque de 15.000 m³, uso exclusivo de riego"
        value={fila.capacidad_descripcion}
        disabled={disabled}
        onChange={(evento) =>
          onCambiar({ capacidad_descripcion: evento.target.value, validada: false })
        }
      />

      <CasillaDeValidacion
        validada={fila.validada}
        nombre={`Validar la obra ${posicion}`}
        disabled={disabled}
        onCambiar={(validada) => onCambiar({ validada })}
      />

      <QuitarEnLinea
        que={`la obra ${posicion}`}
        confirmando={quitando}
        disabled={disabled}
        onPedir={() => setQuitando(true)}
        onCancelar={() => setQuitando(false)}
        onConfirmar={onQuitar}
      />

      {fuenteDeLaPropuesta === undefined ? null : (
        <div className="col-span-full">
          <FuenteDeLaPropuesta fuente={fuenteDeLaPropuesta} />
        </div>
      )}
    </div>
    {terreno}
    </div>
  );
}

/* --- El borrador ----------------------------------------------------------- */

function borradorDesde(informe: Informe): BorradorDeAguas {
  return {
    noAplica: informe.secciones?.agua?.no_aplica ?? false,
    fuentes: filasDesdeFuentes(informe.agua_fuentes ?? []),
    obras: filasDesdeObras(informe.agua_obras ?? []),
    notas: informe.notas?.agua ?? "",
    // LA DESCRIPCIÓN SALE DE LA SECCIÓN, igual que en las otras pestañas
    // técnicas. `null` se vuelve `""`: un `<textarea value={null}>` pasa a no
    // controlado.
    descripcion: informe.secciones?.agua?.descripcion_general ?? "",
    descripcionValidada: informe.secciones?.agua?.descripcion_general_validada ?? false,
  };
}
